/**
 * Pal Detail page — paldeck.cc-style page for a single Pal.
 *
 * Route: /paldex/:slug
 *
 * Sections (top to bottom):
 *   1. Hero — paldeck portrait + name + element badges + rarity
 *   2. Lore description
 *   3. Stats grid (HP / Defense / Attack / Stamina / etc.)
 *   4. Combat skills table (from gamedata: level / power / cooldown)
 *   5. Partner skill card
 *   6. Work suitability badges (icon + level)
 *   7. Drops grid (icon + name)
 *   8. Spawn locations — mini Leaflet map driven by th.gl nodes,
 *      grouped into wild / alpha / variant pins with a colour legend.
 *
 * All data comes from `GET /api/paldex/{slug}` in one round-trip.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, apiError, ASSETS } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader, Loader, RarityBadge } from "@/components/Atoms";
import { ElementChip } from "@/components/PalAssets";
import { ArrowLeft, MapPin, Sword, Hammer, Sparkles, ChevronRight, Share2, Check } from "lucide-react";
import { TILE_SIZE, SCALE, tileToGame } from "@/components/map/markerIcons";

function rarityTier(r) {
  if (!r && r !== 0) return "common";
  if (r >= 15) return "legendary";
  if (r >= 10) return "epic";
  if (r >= 7) return "rare";
  if (r >= 4) return "uncommon";
  return "common";
}

// Spawn-group label → tinted pin colour (matches the main Leaflet map palette).
const GROUP_COLORS = {
  "Wild spawn": "#7FE3C4",
  "Alpha / Boss": "#FFD700",
};
function colorFor(label) {
  if (GROUP_COLORS[label]) return GROUP_COLORS[label];
  if (label.includes("Alpha")) return "#FFD700";
  return "#9D4CDD"; // variants
}

function pickPortrait(pal) {
  const pd = pal?.paldeck;
  const fromPaldeck = pd?.images?.pal_icon;
  if (fromPaldeck) {
    return fromPaldeck.startsWith("http") ? fromPaldeck : `${process.env.REACT_APP_BACKEND_URL}${fromPaldeck}`;
  }
  return pal?.WikiImage || pal?.LocalImage || null;
}

export default function PalDetail() {
  const { slug } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const [pal, setPal] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const livePulseLayerRef = useRef(null);
  const [activeGroups, setActiveGroups] = useState(null); // null = all on
  const [nearby, setNearby] = useState(null);             // { server_name, online, nearby:[...] }
  const [liveOn, setLiveOn] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/paldex/${encodeURIComponent(slug)}`)
      .then(({ data }) => setPal(data))
      .catch((e) => {
        toast.error(apiError(e, "Pal not found"));
        navigate("/lookup");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  // Build the spawn mini-map once the data + DOM are ready.
  useEffect(() => {
    if (!pal || !mapEl.current) return;
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    const map = L.map(mapEl.current, {
      crs: L.CRS.Simple,
      minZoom: 0,
      maxZoom: 5,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer(`${ASSETS}/map/tiles/{z}/{x}/{y}.png`, {
      minZoom: 0, maxZoom: 5, tileSize: TILE_SIZE, noWrap: true,
      bounds: [[-TILE_SIZE, 0], [0, TILE_SIZE]],
    }).addTo(map);
    map.setMaxBounds([[-TILE_SIZE - 32, -32], [32, TILE_SIZE + 32]]);
    // Set a sensible initial view without animation. The marker-render
    // effect below calls invalidateSize + fitBounds once the section is
    // actually visible.
    map.setView([-TILE_SIZE / 2, TILE_SIZE / 2], 0, { animate: false });
    markersLayerRef.current = L.layerGroup().addTo(map);
    livePulseLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
      livePulseLayerRef.current = null;
    };
  }, [pal]);

  // Poll live players near this Pal every 5s while the toggle is on.
  useEffect(() => {
    if (!pal || !liveOn) { setNearby(null); return; }
    let cancelled = false;
    const tick = () => {
      api.get(`/paldex/${encodeURIComponent(pal.slug)}/nearby-players`)
        .then(({ data }) => { if (!cancelled) setNearby(data); })
        .catch(() => { if (!cancelled) setNearby(null); });
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [pal?.slug, liveOn]);

  // Plot live-player pulses on top of the static spawn pins.
  useEffect(() => {
    const layer = livePulseLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!liveOn || !nearby?.nearby?.length) return;
    for (const p of nearby.nearby) {
      const lat = -p.tile_y * SCALE;
      const lng = p.tile_x * SCALE;
      const icon = L.divIcon({
        className: "leaflet-live-player",
        html: `<div class="live-player-pin"><span class="live-player-dot"></span><span class="live-player-label">${p.name} · ~${p.distance_m}m</span></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      L.marker([lat, lng], { icon, zIndexOffset: 1000 })
        .bindTooltip(`<b>${p.name}</b> · Lv ${p.level}<br/>~${p.distance_m}m from nearest spawn`, { direction: "top" })
        .addTo(layer);
    }
  }, [nearby, liveOn]);

  // Render/refresh pins whenever the active filter changes.
  useEffect(() => {
    const map = mapRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer || !pal) return;
    layer.clearLayers();
    const bounds = [];
    for (const g of pal.spawn_groups || []) {
      if (activeGroups && !activeGroups.has(g.label)) continue;
      const color = colorFor(g.label);
      for (const pt of g.points) {
        const lat = -pt.y * SCALE;
        const lng = pt.x * SCALE;
        const icon = L.divIcon({
          className: "leaflet-pal-spawn",
          html: `<div class="spawn-dot" style="background:${color};box-shadow:0 0 8px ${color}cc;"></div>`,
          iconSize: [10, 10],
          iconAnchor: [5, 5],
        });
        L.marker([lat, lng], { icon })
          .bindTooltip(`${g.label} · in-game (${tileToGame(pt.x, pt.y).x}, ${tileToGame(pt.x, pt.y).y})`, { direction: "top" })
          .addTo(layer);
        bounds.push([lat, lng]);
      }
    }
    // Run fitBounds without animation, and only after the map's container
    // has a non-zero size. The page-level mini-map is rendered below the
    // fold so the container can be 0x0 until the user scrolls; if we
    // animate a zoom against a zero-sized map Leaflet trips an internal
    // `_leaflet_pos` race on the next zoom transition.
    const positionView = () => {
      try {
        map.invalidateSize({ animate: false });
        if (bounds.length > 1) {
          map.fitBounds(bounds, { padding: [32, 32], maxZoom: 4, animate: false });
        } else if (bounds.length === 1) {
          map.setView(bounds[0], 3, { animate: false });
        }
      } catch { /* layer was removed mid-frame — harmless */ }
    };

    const container = mapEl.current;
    if (container && "IntersectionObserver" in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          positionView();
          io.disconnect();
        }
      }, { threshold: 0.1 });
      io.observe(container);
      // Safety: still try after a tick in case the section is already in view.
      requestAnimationFrame(positionView);
      return () => io.disconnect();
    }
    requestAnimationFrame(positionView);
  }, [pal, activeGroups]);

  if (loading) return <Loader />;
  if (!pal) return null;

  const portrait = pickPortrait(pal);
  const pd = pal.paldeck;
  const tier = rarityTier(pal.Rarity);
  const partner = pd?.partner_skill || pal.PartnerSkill;
  const drops = pd?.drops || [];
  const work = pd?.work_suitability || [];
  const skills = pal.Skills || [];
  // Stats — prefer paldeck's labelled layout (HP, Defense, Attack, Ranged,
  // Stamina, Support, Craft Speed, Breeding Power) since those are the
  // numbers paldeck.cc displays. Fall back to gamedata when missing.
  const statSrc = pd?.stats && Object.keys(pd.stats).length > 0 ? pd.stats : flattenGamedataStats(pal.Stats || {});

  return (
    <div data-testid="pal-detail-page" className="fade-up">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-[var(--muted)] hover:text-[var(--bone)] flex items-center gap-1 mb-3"
        data-testid="pal-detail-back"
      >
        <ArrowLeft size={12} /> Back
      </button>

      {/* ---- Hero ---- */}
      <div className={`glass-card p-5 sm:p-6 rarity-${tier}`}>
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="shrink-0">
            {portrait ? (
              <img
                src={portrait}
                alt={pal.Name}
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain bg-black/40 rounded-xl border border-white/10 p-2"
                loading="eager"
                data-testid="pal-portrait"
              />
            ) : <div className="w-40 h-40 bg-black/30 rounded-xl" />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl sm:text-4xl mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }} data-testid="pal-name">
              {pal.Name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <RarityBadge rarity={tier} />
              {(pal.Elements || []).map((e) => (
                <ElementChip key={e} element={e} size={14} />
              ))}
              {typeof pal.spawn_total === "number" && pal.spawn_total > 0 && (
                <span className="badge text-[var(--teal)] border-[var(--teal)]/40 inline-flex items-center gap-1">
                  <MapPin size={11} /> {pal.spawn_total} spawn{pal.spawn_total === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {(pd?.lore || pal.Description) && (
              <p className="text-sm text-[var(--muted)] italic leading-relaxed" data-testid="pal-lore">
                {pd?.lore || pal.Description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ---- Stats ---- */}
      {statSrc && Object.keys(statSrc).length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-stats">
          <h3 className="label mb-3 inline-flex items-center gap-2"><Sparkles size={12}/> Stats</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {Object.entries(statSrc).map(([k, v]) => (
              <div key={k} className="bg-black/30 px-3 py-2 rounded border border-white/5">
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{k}</div>
                <div className="glow-teal text-base">{v}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- Combat skills ---- */}
      {skills.length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-skills">
          <h3 className="label mb-3 inline-flex items-center gap-2"><Sword size={12}/> Combat skills</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                  <th className="px-3 py-2 w-16">Lv</th>
                  <th className="px-3 py-2 w-28">Element</th>
                  <th className="px-3 py-2">Skill</th>
                  <th className="px-3 py-2 w-24">Power</th>
                  <th className="px-3 py-2 w-28">Cooldown</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((s) => (
                  <tr key={`${s.Level}-${s.Name}`} className="border-t border-white/5 align-top">
                    <td className="px-3 py-2 text-[var(--teal)] glow-teal">{s.Level}</td>
                    <td className="px-3 py-2" data-testid={`pal-skill-element-${(s.Name || "").replace(/\s/g, "-")}`}>
                      <ElementChip element={s.Element || "Neutral"} size={14} showLabel={true} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[var(--bone)]">{s.Name}</div>
                      {s.Description && (
                        <div className="text-[11px] text-[var(--muted)] leading-relaxed mt-1 max-w-md">
                          {s.Description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{s.Power}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div>{s.Cooldown}s</div>
                      {s.Range && (
                        <div className="text-[10px] text-[var(--muted)] mt-0.5">Range {s.Range}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ---- Partner skill ---- */}
      {partner && (partner.description || partner.Description) && (
        <section className="mt-5 glass-card p-5" data-testid="pal-partner-skill">
          <h3 className="label mb-2">Partner skill</h3>
          <div className="text-sm">
            <div className="text-[var(--bone)] mb-1">{partner.name || partner.Name || "Partner Skill"}</div>
            <p className="text-[var(--muted)] leading-relaxed">{partner.description || partner.Description}</p>
          </div>
        </section>
      )}

      {/* ---- Innate passive skills ---- (bosses / alphas only) */}
      {(pal.PassiveSkills || []).length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-passive-skills">
          <h3 className="label mb-3">Innate passives</h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {pal.PassiveSkills.map((p, i) => (
              <div
                key={`${p.name || p.Name}-${i}`}
                className="bg-black/30 border border-white/10 rounded p-3"
                data-testid={`pal-passive-${(p.name || p.Name || "").replace(/\s/g, "-").toLowerCase()}`}
              >
                <div className="text-sm text-[var(--bone)] flex items-center gap-2">
                  <span>{p.name || p.Name}</span>
                  {p.rank != null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                      p.rank >= 3 ? "text-amber-300 border-amber-300/40 bg-amber-300/5" :
                      p.rank > 0  ? "text-[var(--teal)] border-[var(--teal-soft)]" :
                                    "text-red-300 border-red-300/40 bg-red-300/5"
                    }`}>
                      Rank {p.rank > 0 ? `+${p.rank}` : p.rank}
                    </span>
                  )}
                </div>
                {(p.description || p.Description) && (
                  <p className="text-[11px] text-[var(--muted)] leading-relaxed mt-1">
                    {p.description || p.Description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- Work suitability ---- */}
      {work.length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-work">
          <h3 className="label mb-3 inline-flex items-center gap-2"><Hammer size={12}/> Work suitability</h3>
          <div className="flex flex-wrap gap-2">
            {work.map((w) => (
              <div key={w.name} className="bg-black/30 border border-white/10 rounded px-2 py-1 flex items-center gap-2">
                {w.icon && <img src={`${process.env.REACT_APP_BACKEND_URL}${w.icon}`} alt={w.name} className="w-6 h-6 object-contain" loading="lazy" />}
                <span className="text-xs">{w.name}</span>
                <span className="text-xs text-[var(--teal)] glow-teal">Lv {w.level}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- Drops ---- */}
      {drops.length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-drops">
          <h3 className="label mb-3">Drops</h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {drops.map((d) => (
              <div key={d.name} className="bg-black/30 rounded border border-white/10 p-2 text-center">
                {d.icon && <img src={`${process.env.REACT_APP_BACKEND_URL}${d.icon}`} alt={d.name} className="w-12 h-12 object-contain mx-auto mb-1" loading="lazy" />}
                <div className="text-xs truncate" title={d.name}>{d.name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- Spawn map ---- */}
      {(pal.spawn_groups || []).length > 0 && (
        <section className="mt-5 glass-card p-5" data-testid="pal-spawn-map">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="label inline-flex items-center gap-2 !mb-0"><MapPin size={12}/> Locations</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <NearbyLiveBadge nearby={nearby} liveOn={liveOn} onToggle={() => setLiveOn((v) => !v)} />
              <ShareLocationButton pal={pal} />
              <Link to="/map" className="text-[10px] uppercase tracking-wider text-[var(--teal)] hover:underline inline-flex items-center gap-1">
                Open full map <ChevronRight size={10}/>
              </Link>
            </div>
          </div>
          {/* Group filter chips */}
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              onClick={() => setActiveGroups(null)}
              className={`badge !text-[10px] ${activeGroups === null ? "border-[var(--teal)] text-[var(--teal)]" : "border-white/10 text-[var(--muted)]"}`}
              data-testid="spawn-filter-all"
            >
              All
            </button>
            {pal.spawn_groups.map((g) => {
              const on = activeGroups === null || activeGroups.has(g.label);
              return (
                <button
                  key={g.label + g.type}
                  onClick={() => {
                    setActiveGroups((prev) => {
                      const cur = new Set(prev || pal.spawn_groups.map((x) => x.label));
                      if (cur.has(g.label)) cur.delete(g.label); else cur.add(g.label);
                      return cur.size === 0 ? new Set([g.label]) : cur;
                    });
                  }}
                  className="badge !text-[10px] inline-flex items-center gap-1"
                  style={{
                    borderColor: on ? `${colorFor(g.label)}66` : "rgba(255,255,255,0.1)",
                    color: on ? colorFor(g.label) : "var(--muted)",
                  }}
                  data-testid={`spawn-filter-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: colorFor(g.label) }} />
                  {g.label} ({g.count})
                </button>
              );
            })}
          </div>
          <div ref={mapEl} className="w-full rounded-lg overflow-hidden border border-white/10" style={{ height: 420, background: "#06030A" }} />
        </section>
      )}
    </div>
  );
}

/** Convert gamedata's nested stats ({HP: 120, Attack: {Melee: 130, Ranged: 130}, ...})
 * into the flat label→number layout used by the UI. */
function flattenGamedataStats(stats) {
  if (!stats || typeof stats !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(stats)) {
    if (typeof v === "number") out[k] = v;
  }
  if (stats.Attack && typeof stats.Attack === "object") {
    if (stats.Attack.Melee != null) out["Attack"] = stats.Attack.Melee;
    if (stats.Attack.Ranged != null) out["Ranged"] = stats.Attack.Ranged;
  }
  return out;
}


/**
 * Share-to-clipboard button. Builds a tidy "Where to find <Pal>" snippet
 * — pal name, spawn-group totals, up to three in-game coord samples per
 * group, and the URL back to this page — then copies it so the user can
 * paste it straight into Discord (via the bot's chat-bridge or anywhere
 * else). In-game coords only; tile-space numbers are an internal detail.
 */
function ShareLocationButton({ pal }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  const buildMessage = () => {
    const url = `${window.location.origin}/paldex/${pal.slug}`;
    const total = pal.spawn_total || 0;
    const lines = [];
    lines.push(`📍 **${pal.Name}** — ${total} known spawn${total === 1 ? "" : "s"}`);
    for (const g of pal.spawn_groups || []) {
      const samples = g.points.slice(0, 3).map((pt) => {
        const ig = tileToGame(pt.x, pt.y);
        return `(${ig.x}, ${ig.y})`;
      });
      lines.push(`• ${g.label} (${g.count}): ${samples.join(", ")}${g.count > 3 ? " …" : ""}`);
    }
    lines.push(`🗺️ ${url}`);
    return lines.join("\n");
  };

  const onClick = async () => {
    const msg = buildMessage();
    try {
      await navigator.clipboard.writeText(msg);
      setCopied(true);
      toast.success("Copied to clipboard — paste it into Discord");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  // Disable the button if there's nothing useful to share.
  const disabled = !(pal.spawn_groups || []).length;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="text-[10px] uppercase tracking-wider text-[var(--teal)] hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
      title={disabled ? "No spawn data to share" : "Copy a Discord-ready snippet to your clipboard"}
      data-testid="share-location-button"
    >
      {copied ? <><Check size={11} /> Copied</> : <><Share2 size={11} /> Share location</>}
    </button>
  );
}


/**
 * Tiny live indicator showing how many players are currently within
 * ~200 m of one of this Pal's spawn points. Polls the backend every 5s
 * (see the parent useEffect). Click to mute / un-mute the overlay.
 */
function NearbyLiveBadge({ nearby, liveOn, onToggle }) {
  const count = nearby?.nearby?.length || 0;
  const offline = nearby && nearby.online === false;
  const label = !liveOn
    ? "Live overlay off"
    : offline
      ? "Server offline"
      : count === 0
        ? "No players nearby"
        : `${count} player${count === 1 ? "" : "s"} nearby`;
  const color = !liveOn ? "var(--muted)" : count > 0 ? "#00FFAA" : "var(--muted)";
  return (
    <button
      onClick={onToggle}
      className="text-[10px] uppercase tracking-wider hover:underline inline-flex items-center gap-1.5"
      style={{ color }}
      title={liveOn ? "Mute the live overlay" : "Show players near this Pal's spawn points"}
      data-testid="nearby-live-badge"
    >
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{
          background: liveOn && count > 0 ? "#00FFAA" : "rgba(255,255,255,0.25)",
          boxShadow: liveOn && count > 0 ? "0 0 8px #00FFAA" : "none",
        }}
      />
      {label}
    </button>
  );
}
